"use client";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogClose,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ITheme, IUserData } from "@/types";
import { parseAsString, useQueryState } from "nuqs";
import { useRouter, usePathname } from "next/navigation";

import {
    IconShare2,
    IconBrandFacebookFilled,
    IconBrandXFilled,
    IconBrandLinkedinFilled,
    IconBrandReddit,
    IconMail,
    IconCopy,
    IconSparkles,
    IconQrcode,
} from "@tabler/icons-react";
import {
    FacebookShareButton,
    TwitterShareButton,
    LinkedinShareButton,
    EmailShareButton,
    RedditShareButton,
} from "react-share";
import { toast } from "sonner";
interface Props {
    theme: ITheme;
    user: IUserData;
    admin: boolean;
    customTrigger?: React.ReactNode;
}
export default function ProfileShare({
    theme,
    user,
    admin,
    customTrigger,
}: Props) {
    const router = useRouter();
    const path = usePathname();
    const firstPathSegment = path.split("/")[1];
    const lastPathSegment = path.split("/").pop();

    const profileUrl = user.domain.verified
        ? user.domain.domain +
          "/" +
          (firstPathSegment === "admin"
              ? lastPathSegment
                    ?.replace(`${user.username}`, "")
                    .replace("content", "")
              : lastPathSegment?.replace(`${user.username}`, ""))
        : `doras.to/${user.username}/${lastPathSegment == user.username || lastPathSegment == "content" ? "" : lastPathSegment}`;
    const profileUrlHttp = "https://" + profileUrl;
    const [selectedGlobalItem, setSelectedGlobalItem] =
        (admin && useQueryState("cc", parseAsString.withDefault(""))) || [];
    const trackShareClick = (platform: string) => {
        window.dispatchEvent(
            new CustomEvent("user-profile-share-click-track", {
                detail: {
                    platform: platform,
                },
            })
        );
    };
    return (
        <Dialog>
            <DialogTrigger asChild>
                {customTrigger ? (
                    customTrigger
                ) : (
                    <Button
                        className={`${theme.button_rounded} sticky top-4 z-[99999999999] w-full gap-1 bg-transparent text-base font-bold hover:bg-transparent`}
                        style={{
                            background: theme.button
                                ? theme.button
                                : user.theme.button,
                            color: theme.text_col_button
                                ? theme.text_col_button
                                : user.theme.text_col_button,
                        }}
                    >
                        <IconShare2 size={32} /> Share
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent
                className="h-fit w-80 p-3 sm:h-fit sm:max-h-[80dvh] sm:w-80"
                style={{
                    borderColor: user.theme.button,
                }}
            >
                <DialogTitle></DialogTitle>
                <div className="h-full overflow-auto">
                    <div className="flex flex-col items-center gap-1">
                        <img
                            src={user.pic}
                            className="size-14 rounded-full"
                            alt="User Profile"
                        />
                        <Label
                            variant={"headingtoggle"}
                            className="text-center"
                        >
                            Share {user.displayname}'s profile
                        </Label>
                    </div>
                    <Button
                        id="Copy URL"
                        className={`h-auto w-full scale-95 justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                        onClick={async () => {
                            try {
                                trackShareClick("author");
                                toast.success("Copied to clipboard!");
                                admin
                                    ? await navigator.clipboard.writeText(
                                          profileUrlHttp
                                      )
                                    : await navigator.clipboard.writeText(
                                          profileUrlHttp
                                      );
                            } catch (err) {
                                console.error("Failed to copy text: ", err);
                                toast.error("Failed to copy to clipboard");
                            }
                        }}
                    >
                        <IconCopy
                            size={25}
                            className="rounded-md bg-primary p-1"
                        />{" "}
                        {profileUrl}
                        {/* {shareUrlNoHttp} */}
                    </Button>
                    {admin && (
                        <>
                            <DialogClose asChild>
                                <Button
                                    id="QR"
                                    className={`h-auto w-full scale-95 justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                                    onClick={() => {
                                        setSelectedGlobalItem?.("QR Code");
                                    }}
                                >
                                    <IconQrcode
                                        size={25}
                                        className="rounded-md bg-primary p-1"
                                    />{" "}
                                    QR code & other tools
                                </Button>
                            </DialogClose>
                        </>
                    )}
                    <Separator className="my-3" />
                    <div className="flex flex-col gap-1">
                        <FacebookShareButton
                            url={profileUrlHttp}
                            className="w-full"
                        >
                            <div
                                onClick={() => trackShareClick("facebook")}
                                id="Facebook"
                                className={`flex h-auto w-full scale-95 flex-row items-center justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                            >
                                <IconBrandFacebookFilled
                                    className="rounded-md bg-[#217cf4] p-1"
                                    size={25}
                                />
                                Share on Facebook
                            </div>
                        </FacebookShareButton>
                        <TwitterShareButton
                            url={profileUrlHttp}
                            className="w-full"
                            title={`Check out ${user.displayname}'s profile on Doras!`}
                            hashtags={["Doras", "Profile", "LinkInBio"]}
                            related={["doras_to"]}
                        >
                            <div
                                onClick={() => trackShareClick("x")}
                                id="Twitter"
                                className={`flex h-auto w-full scale-95 flex-row items-center justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                            >
                                <IconBrandXFilled
                                    size={25}
                                    className="rounded-md bg-black p-1"
                                />{" "}
                                <>Share on Twitter</>
                            </div>
                        </TwitterShareButton>
                        <LinkedinShareButton
                            url={profileUrlHttp}
                            className="w-full"
                        >
                            <div
                                onClick={() => trackShareClick("linkedin")}
                                id="LinkedIn"
                                className={`flex h-auto w-full scale-95 flex-row items-center justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                            >
                                <IconBrandLinkedinFilled
                                    size={25}
                                    className="rounded-md bg-[#087fb7] p-1"
                                />{" "}
                                <>Share on LinkedIn</>
                            </div>
                        </LinkedinShareButton>
                        <RedditShareButton
                            url={profileUrlHttp}
                            className="w-full"
                        >
                            <div
                                onClick={() => trackShareClick("reddit")}
                                id="Reddit"
                                className={`flex h-auto w-full scale-95 flex-row items-center justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                            >
                                <IconBrandReddit
                                    size={25}
                                    className="rounded-md bg-[#ff4b08] p-1"
                                />{" "}
                                <>Share on Reddit</>
                            </div>
                        </RedditShareButton>
                        <EmailShareButton
                            url={profileUrlHttp}
                            className="w-full"
                        >
                            <div
                                onClick={() => trackShareClick("email")}
                                id="Email"
                                className={`flex h-auto w-full scale-95 flex-row items-center justify-start gap-1 rounded-md bg-muted p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                            >
                                <IconMail
                                    size={25}
                                    className="rounded-md bg-red-400 p-1"
                                />{" "}
                                <>Email this</>
                            </div>
                        </EmailShareButton>
                    </div>
                    <Separator className="my-3" />
                    {!admin && user.settings.branding === false && (
                        <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-2">
                                <div className="rounded-md bg-primary p-1">
                                    <img
                                        src="https://cdn.doras.to/doras/icon-white.svg"
                                        className="h-4 w-4"
                                        alt="Doras Logo"
                                    />
                                </div>
                                <Label variant={"toggle"}>
                                    Create your profile in minutes
                                </Label>
                            </div>
                            <a
                                href={
                                    user.affiliate
                                        ? `https://doras.to?aff=${user.affiliate}`
                                        : "https://doras.to"
                                }
                                target="_blank"
                                rel="noreferrer"
                            >
                                <Button variant={"outline"}>
                                    Create Yours
                                </Button>
                            </a>
                        </div>
                    )}
                    {!admin && (
                        <div className="!visible flex w-full items-center rounded-md pt-3 !text-foreground">
                            <Button
                                className={`!visible mx-auto w-fit rounded-md border !bg-background !text-foreground`}
                                onClick={() => {
                                    router.push(
                                        `https://doras.to/report?user=${user.id}`
                                    );
                                }}
                            >
                                Report profile
                            </Button>
                        </div>
                    )}
                    {admin &&
                        (user.account_type === "user" ||
                            user.account_type === "bfree") && (
                            <div className="my-3 flex flex-col gap-1 rounded-md border border-primary bg-primary/10 p-1">
                                <div className="flex items-center">
                                    <IconSparkles size={16} />
                                    <Label variant={"toggle"}>Upgrade</Label>
                                </div>
                                <Label variant={"subtoggle"}>
                                    Upgrade to start using your own domain, hide
                                    Doras branding and more!
                                </Label>
                                <Button
                                    className={`h-auto w-full scale-95 gap-1 rounded-md bg-gradient-to-br from-primary from-25% to-tertiary p-2 text-sm font-bold transition-all duration-150 hover:scale-100 hover:bg-muted`}
                                    onClick={() => {
                                        if (user.account_type === "user") {
                                            setSelectedGlobalItem?.("Join Pro");
                                        } else {
                                            router.push(
                                                `/admin/brand/${user.id}/settings/billing`
                                            );
                                        }
                                    }}
                                >
                                    Learn more
                                </Button>
                            </div>
                        )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
